import { z } from 'zod';
import { emailSchema } from '../lib/validation';

/**
 * AB#1845 (PBI-E) / AB#1838 — Gender-matrix rollout.
 *
 * Staff gender is REQUIRED at invite time so the customer-side gender
 * matrix (staff sex × salon type × service type) can correctly publish
 * a staff member. Owners can no longer save without picking Male or
 * Female. The Zod enum below is the FE gate; the BE validator is the
 * authority and returns `STAFF_GENDER_REQUIRED` when missing — which
 * the modal surfaces in the form's error region with the same wording.
 *
 * The `Gender.Other` (=3) enum value is intentionally not selectable
 * here: per the Gender Matrix Wiki §3 the customer-side gender axis is
 * a binary (male staff / female staff), and listing "Other" with no
 * mapping into that axis would create an unbookable staff record.
 */
export const STAFF_GENDER_MALE = 1 as const;
export const STAFF_GENDER_FEMALE = 2 as const;

/**
 * Zod literal-union enforces required + binary. Saving with no gender
 * picked fails parse with the custom message `gender_required`, which
 * the modal then maps to the localized `staff.errors.genderRequired`
 * string. Matches Sara's BE error code STAFF_GENDER_REQUIRED.
 */
export const staffGenderSchema = z.union(
  [z.literal(STAFF_GENDER_MALE), z.literal(STAFF_GENDER_FEMALE)],
  { errorMap: () => ({ message: 'gender_required' }) },
);

export const staffSchema = z.object({
  fullName: z.string().min(2, 'Full name must be at least 2 characters').max(100),
  email: emailSchema,
  phoneNumber: z.string().min(7, 'Phone number is required').max(20),
  position: z.string().optional(),
  role: z.union([z.literal(1), z.literal(2)]).optional(),
  gender: staffGenderSchema,
  isActive: z.boolean().optional(),
  branchIds: z.array(z.number()).optional(),
});

export type StaffForm = z.infer<typeof staffSchema>;
